/**
 * Step 2 — Dosimetry Planning.
 *
 * Lists perfused volumes as collapsible cards. Each card shows a job status
 * badge in the header, with the dose table revealed inline when the card
 * is selected (expanded).
 */

import { AlertCircle, Circle, Loader2, Play, CheckCircle2, RotateCcw } from 'lucide-react';
import type { MouseEvent } from 'react';
import { useParams } from 'react-router';

import { DoseTableView } from './dose-table-view';
import { PerfusedVolumeCard } from './perfused-volume-card';
import type { IDoseTable } from '../domain/dose-table';
import { useDosimetryJobs, type DosimetryJobStatus } from '../hooks/use-dosimetry-jobs';
import { useDosimetryState } from '../hooks/use-dosimetry-state';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

// ============================================================================
// Component
// ============================================================================

export function DosimetryPlanningPanel() {
    const { caseId } = useParams<{ caseId: string }>();
    const state = useDosimetryState();
    const jobs = useDosimetryJobs(caseId);

    if (!state) {
        return (
            <div className="flex flex-col gap-4 p-4">
                <p className="text-xs text-muted-foreground">Loading dosimetry data...</p>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto">
                <div className="flex w-full items-center gap-1 px-2 py-2 text-sm font-semibold font-mono uppercase tracking-wide text-muted-foreground">
                    [Perfused Volumes]
                </div>
                <div className="flex flex-col gap-1.5 px-2 pb-2">
                    {state.perfusedVolumes.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground px-1">
                            No perfused volumes. Go to Segmentation step to create them.
                        </p>
                    ) : (
                        state.perfusedVolumes.map((pv) => {
                            const isSelected = state.selectedPvId === pv.id;
                            const jobStatus = jobs.jobStatus.get(pv.id) ?? 'idle';
                            const jobError = jobs.jobErrors.get(pv.id);
                            const doseTable = state.doseTable(pv.id);
                            const adjustedDose = state.adjustedDose(pv.id);
                            const isRunning = jobStatus === 'queued' || jobStatus === 'processing';

                            const handleRun = (e: MouseEvent) => {
                                e.stopPropagation();
                                state.selectPerfusedVolume(pv.id);
                                jobs.runDosimetry(pv.id, [...pv.segmentIndices], [...pv.tumorIndices]);
                            };

                            return (
                                <Collapsible key={pv.id} open={isSelected}>
                                    <PerfusedVolumeCard
                                        pv={pv}
                                        isSelected={isSelected}
                                        onSelect={state.selectPerfusedVolume}
                                        headerExtra={<PvHeaderExtra jobStatus={jobStatus} />}
                                    >
                                        <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
                                            {/* stopPropagation prevents clicks inside from toggling selection */}
                                            <div onClick={stop} className="pt-2">
                                                <PvCollapsibleBody
                                                    doseTable={doseTable}
                                                    adjustedDose={adjustedDose}
                                                    jobStatus={jobStatus}
                                                    jobError={jobError}
                                                    isRunning={isRunning}
                                                    onActivityChange={(actGBq) =>
                                                        state.setAdjustedDose(pv.id, actGBq)
                                                    }
                                                    onRestoreReference={() => state.clearAdjustedDose(pv.id)}
                                                    onRun={handleRun}
                                                />
                                            </div>
                                        </CollapsibleContent>
                                    </PerfusedVolumeCard>
                                </Collapsible>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// PvHeaderExtra — job status badge in card header
// ============================================================================

function PvHeaderExtra({ jobStatus }: { jobStatus: DosimetryJobStatus }) {
    return (
        <span className="flex items-center gap-1" onClick={stop}>
            <JobStatusBadge status={jobStatus} />
        </span>
    );
}

// ============================================================================
// PvCollapsibleBody — content inside the collapsible
// ============================================================================

function PvCollapsibleBody({
    doseTable,
    adjustedDose,
    jobStatus,
    jobError,
    isRunning,
    onActivityChange,
    onRestoreReference,
    onRun,
}: {
    doseTable: IDoseTable | undefined;
    adjustedDose: { injectedActivityGBq: number } | undefined;
    jobStatus: DosimetryJobStatus;
    jobError: string | undefined;
    isRunning: boolean;
    onActivityChange: (actGBq: number) => void;
    onRestoreReference: () => void;
    onRun: (e: MouseEvent) => void;
}) {
    if (doseTable) {
        return (
            <div className="flex flex-col gap-2">
                <DoseTableView
                    referenceTable={doseTable}
                    adjustedActivityGBq={adjustedDose?.injectedActivityGBq}
                    onActivityChange={onActivityChange}
                />
                {adjustedDose && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-xs self-end"
                        onClick={onRestoreReference}
                    >
                        <RotateCcw className="h-3 w-3" />
                        Restore reference values
                    </Button>
                )}
            </div>
        );
    }

    if (isRunning) {
        return (
            <div className="flex flex-col items-center gap-2 py-4 text-xs text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <p>{jobStatus === 'queued' ? 'Job queued...' : 'Computing dosimetry...'}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center gap-2 py-4 text-xs text-muted-foreground">
            {jobError && (
                <p className="flex items-center gap-1.5 text-destructive">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {jobError}
                </p>
            )}
            <p>Run dosimetry to compute dose distribution.</p>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={onRun}>
                <Play className="h-3 w-3" />
                Run Dosimetry
            </Button>
        </div>
    );
}

// ============================================================================
// Helpers
// ============================================================================

/** Prevent clicks from bubbling up to the card's toggle handler. */
function stop(e: MouseEvent) {
    e.stopPropagation();
}

// ============================================================================
// JobStatusBadge
// ============================================================================

function JobStatusBadge({ status }: { status: DosimetryJobStatus }) {
    if (status === 'idle') {
        return (
            <Badge variant="outline" className="text-[10px] text-muted-foreground border-muted-foreground/30">
                <Circle className="h-2.5 w-2.5 mr-0.5" />
                Not run
            </Badge>
        );
    }

    if (status === 'completed') {
        return (
            <Badge variant="outline" className="text-[10px] text-green-600 border-green-600/30">
                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                Done
            </Badge>
        );
    }

    if (status === 'failed') {
        return (
            <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">
                <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                Failed
            </Badge>
        );
    }

    return (
        <Badge variant="outline" className="text-[10px]">
            <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" />
            {status === 'queued' ? 'Queued' : 'Running'}
        </Badge>
    );
}
